import { EnhancedUnifiedFormData } from '@/validation/enhancedUnifiedSchemas';
import { calculateAnnualIncome } from '@/utils/unified/dataTransformers';

export interface BudgetCategory {
  name: string;
  currentAmount: number;
  withMortgageAmount: number;
  isEssential: boolean;
  adjustable: boolean;
  percentage: number;
}

export interface BudgetImpact {
  monthlyIncome: number;
  totalMonthlyDebt: number;
  mortgagePayment: number;
  categories: BudgetCategory[];
  disposableIncomeBefore: number;
  disposableIncomeAfter: number;
  savingsRate: number;
  emergencyFundMonths: number;
  recommendations: string[];
}

export const calculateBudgetImpact = (
  formData: EnhancedUnifiedFormData,
  mortgagePayment: number
): BudgetImpact => {
  const monthlyIncome = calculateAnnualIncome(formData) / 12;
  
  // Calculate total monthly debt
  const studentLoanPayment = (formData.debts?.studentLoanBalance || 0) * 0.01; // Estimate 1% of balance as monthly payment
  const totalMonthlyDebt = 
    (formData.debts?.autoLoanPayment || 0) +
    (formData.debts?.creditCardPayment || 0) +
    studentLoanPayment +
    (formData.debts?.personalLoanPayment || 0) +
    (formData.debts?.childSupportPayment || 0) +
    (formData.debts?.otherDebts || 0);

  // Essential expenses (estimated percentages)
  const utilities = monthlyIncome * 0.10;
  const groceries = monthlyIncome * 0.12;
  
  // Discretionary spending (current)
  const diningOut = monthlyIncome * 0.06;
  const entertainment = monthlyIncome * 0.05;
  const travel = monthlyIncome * 0.04;
  const shopping = monthlyIncome * 0.06;
  
  // Calculate disposable income
  const currentEssential = totalMonthlyDebt + utilities + groceries;
  const withMortgageEssential = mortgagePayment + totalMonthlyDebt + utilities + groceries;
  
  const disposableIncomeBefore = monthlyIncome - currentEssential;
  const disposableIncomeAfter = monthlyIncome - withMortgageEssential;
  
  // Calculate savings
  const currentSavings = Math.max(0, disposableIncomeBefore - (diningOut + entertainment + travel + shopping));
  const projectedSavings = Math.max(0, disposableIncomeAfter * 0.15);
  const savingsRate = (projectedSavings / monthlyIncome) * 100;
  
  // Emergency fund calculation
  const emergencyFundMonths = currentSavings > 0 ? (currentSavings * 12) / (withMortgageEssential + (monthlyIncome * 0.15)) : 0;

  // Build categories
  const categories: BudgetCategory[] = [
    {
      name: 'Housing',
      currentAmount: 0,
      withMortgageAmount: mortgagePayment,
      isEssential: true,
      adjustable: false,
      percentage: (mortgagePayment / monthlyIncome) * 100
    },
    {
      name: 'Debt Payments',
      currentAmount: totalMonthlyDebt,
      withMortgageAmount: totalMonthlyDebt,
      isEssential: true,
      adjustable: false,
      percentage: (totalMonthlyDebt / monthlyIncome) * 100
    },
    {
      name: 'Utilities & Insurance',
      currentAmount: utilities,
      withMortgageAmount: utilities,
      isEssential: true,
      adjustable: false,
      percentage: (utilities / monthlyIncome) * 100
    },
    {
      name: 'Groceries',
      currentAmount: groceries,
      withMortgageAmount: groceries,
      isEssential: true,
      adjustable: false,
      percentage: (groceries / monthlyIncome) * 100
    },
    {
      name: 'Dining Out',
      currentAmount: diningOut,
      withMortgageAmount: Math.max(monthlyIncome * 0.03, 100),
      isEssential: false,
      adjustable: true,
      percentage: 3
    },
    {
      name: 'Entertainment',
      currentAmount: entertainment,
      withMortgageAmount: Math.max(monthlyIncome * 0.02, 50),
      isEssential: false,
      adjustable: true,
      percentage: 2
    },
    {
      name: 'Travel',
      currentAmount: travel,
      withMortgageAmount: Math.max(monthlyIncome * 0.02, 100),
      isEssential: false,
      adjustable: true,
      percentage: 2
    },
    {
      name: 'Shopping',
      currentAmount: shopping,
      withMortgageAmount: Math.max(monthlyIncome * 0.03, 75),
      isEssential: false,
      adjustable: true,
      percentage: 3
    },
    {
      name: 'Savings',
      currentAmount: currentSavings,
      withMortgageAmount: projectedSavings,
      isEssential: true,
      adjustable: false,
      percentage: savingsRate
    }
  ];

  // Generate recommendations
  const recommendations: string[] = [];
  
  if (disposableIncomeAfter < monthlyIncome * 0.10) {
    recommendations.push('Consider reducing discretionary spending or targeting a lower home price');
  }
  
  if (savingsRate < 10) {
    recommendations.push('Increase savings cushion before purchasing to avoid financial stress');
  }
  
  if ((totalMonthlyDebt / monthlyIncome) * 100 > 20) {
    recommendations.push('Focus on debt reduction before taking on mortgage');
  }
  
  if (emergencyFundMonths < 3) {
    recommendations.push('Build emergency fund to at least 3-6 months of expenses');
  }
  
  const discretionaryReduction = (diningOut - (monthlyIncome * 0.03));
  if (discretionaryReduction > 0) {
    recommendations.push(`Reducing dining out by $${Math.round(discretionaryReduction)}/month would improve budget flexibility`);
  }

  return {
    monthlyIncome,
    totalMonthlyDebt,
    mortgagePayment,
    categories,
    disposableIncomeBefore,
    disposableIncomeAfter,
    savingsRate,
    emergencyFundMonths,
    recommendations
  };
};
